import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

// Loads the next page of a group's rows as a turbo-stream and lets Turbo apply it —
// the stream inserts the next page before this sentinel and replaces it with the
// following one (or removes it when the group is exhausted).
//
// FD-428: auto-loads on scroll (infinite scroll). An IntersectionObserver watches
// the sentinel and calls #load when it nears the viewport, so a fresh page is
// fetched as the user scrolls down; clicking the button is the fallback (and the
// only path if IntersectionObserver is unavailable). On success the sentinel is
// replaced/removed and this controller disconnects — the new sentinel connects its
// own observer, so paging advances one page at a time as content fills the screen.
// FD-168 collapsed groups keep their sentinel inside a hidden body, so it never
// intersects until the group is expanded.
//
// FD-495: auto:false opts out of the scroll auto-load — click-only. Used by the
// activity feed's "Show earlier" trigger, which sits at the TOP of its container
// (each older page is inserted after it), where observer-driven loading could
// cascade back through the whole history.
export default class extends Controller {
  static values = { url: String, auto: { type: Boolean, default: true } }

  connect() {
    if (!this.autoValue) return
    if (typeof IntersectionObserver === "undefined") return
    // rootMargin pre-fetches just before the sentinel scrolls into view; once a
    // page lands it pushes the (new) sentinel back out of range, so we fetch one
    // page per scroll rather than the whole group at once.
    this.observer = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) this.load()
    }, { rootMargin: "300px" })
    this.observer.observe(this.element)
  }

  disconnect() {
    this.observer?.disconnect()
  }

  // FD-630: "loading" lives on the element, not in a JS boolean. The button is
  // deliberately left disabled until the stream replaces it — but a broadcast
  // morph can REUSE this node and strip disabled/aria-busy (the server never
  // renders them) while the controller instance survives. A boolean would then
  // claim "loading" forever about a button that looks live, deadening both the
  // click path and the observer until a reload. The same morph strips the data
  // attribute too, so the surviving controller re-arms along with the DOM.
  get loading() {
    return this.element.hasAttribute("data-loading")
  }

  async load(event) {
    event?.preventDefault()
    if (this.loading) return
    this.element.setAttribute("data-loading", "")
    this.element.disabled = true
    this.element.setAttribute("aria-busy", "true")
    try {
      const response = await fetch(this.urlValue, {
        headers: { Accept: "text/vnd.turbo-stream.html" }
      })
      if (response.ok) {
        Turbo.renderStreamMessage(await response.text())
      } else {
        this.reset()
      }
    } catch {
      this.reset()
    }
  }

  // Re-enable on a transient failure so the user can click again. Also stop the
  // observer so a persistently-failing fetch can't auto-retry in a tight loop —
  // the button stays clickable as a manual fallback.
  reset() {
    this.element.removeAttribute("data-loading")
    this.element.disabled = false
    this.element.removeAttribute("aria-busy")
    this.observer?.disconnect()
  }
}
